import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Activity } from 'lucide-react';
import apiService from '../../services/apiService';
import { getActiveForecastUser } from './forecastUsers';
import { errText } from './ForecastProjects';
import {
  ForecastWorkspaceShell,
  WORKSPACE_READINESS,
  useUnsavedChangesWarning,
  useWorkspaceStage
} from './ForecastWorkspace';
import CorporateProjectExtractStage from './stages/CorporateProjectExtractStage';
import CorporateProjectReviewStage from './stages/CorporateProjectReviewStage';
import CorporateProjectSourceStage from './stages/CorporateProjectSourceStage';
import CorporateProjectSpreadStage, { validateProjectSpreadClient } from './stages/CorporateProjectSpreadStage';

const CorporateProjectProFormaStage = React.lazy(() => import('./stages/CorporateProjectProFormaStage'));
const ProjectPdfPanel = React.lazy(() => import('./ProjectPdfPanel'));

// Corporate project workspace: PRJ_01 source intake (upload -> extraction),
// PRJ_02 template (marginal revenue / incremental opex / bucketed capex),
// PRJ_03 server-computed pro forma, PRJ_04 review & submit.

const newId = () => `line-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
const formatMM = (value) => `${Number(value) < 0 ? '-$' : '$'}${Math.abs(Number(value) || 0).toFixed(1)}MM`;
const formatTimestamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const ForecastProjectWorkspace = () => {
  const { projectId } = useParams();
  const activeUser = getActiveForecastUser();
  const [project, setProject] = useState(null);
  const [template, setTemplate] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [capexBuckets, setCapexBuckets] = useState([]);
  const [capexBucketError, setCapexBucketError] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);          // 'upload' | 'draft' | 'save' | 'load'
  const [guidance, setGuidance] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [activeCitation, setActiveCitation] = useState(null);   // {id, ts}
  const [reviewCommentary, setReviewCommentary] = useState('');
  const [returnComment, setReturnComment] = useState('');
  const [projectSaveState, setProjectSaveState] = useState('saved');
  const fileRef = useRef(null);
  const saveTimer = useRef(null);
  const pendingTemplateRef = useRef(null);
  const pendingCommentaryRef = useRef(null);
  const undoTemplateRef = useRef(null);
  const [spreadImportPreview, setSpreadImportPreview] = useState(null);

  const loadCapexBuckets = useCallback(() => {
    setCapexBucketError('');
    return apiService.fetchProjectOpenIds()
      .then((ids) => setCapexBuckets(ids.capexBuckets || []))
      .catch((err) => setCapexBucketError(errText(err) || 'Capitalization categories could not be loaded.'));
  }, []);

  useEffect(() => {
    apiService.fetchProject(projectId)
      .then((record) => {
        setProject(record);
        setTemplate(record.template);
        setFinancials(record.financials);
        setReviewCommentary(record.review_commentary || '');
        undoTemplateRef.current = null;
        setSpreadImportPreview(null);
      })
      .catch((err) => setError(errText(err)));
    loadCapexBuckets();
  }, [loadCapexBuckets, projectId]);

  const editable = project && ['draft', 'in_review'].includes(project.status);
  const extraction = project?.extraction;
  const aiDraft = project?.ai_draft;
  const spreadValidation = useMemo(
    () => validateProjectSpreadClient(template || {}, extraction?.durationMonths),
    [extraction?.durationMonths, template]
  );
  const spreadBlocked = Boolean(template && (spreadValidation.errors || []).length > 0);
  const invalidLineSummary = useMemo(() => {
    const names = Array.from(new Set((spreadValidation.errors || []).map((issue) =>
      issue.lineDescription || issue.lineId).filter(Boolean)));
    return names.slice(0, 4).join(', ') + (names.length > 4 ? `, +${names.length - 4} more` : '');
  }, [spreadValidation.errors]);

  const persistPendingChanges = async (throwOnError = false) => {
    const pendingTemplate = pendingTemplateRef.current;
    const pendingCommentary = pendingCommentaryRef.current;
    if (!pendingTemplate && pendingCommentary === null) return;
    setBusy('save');
    setProjectSaveState('saving');
    try {
      const result = await apiService.saveProject(projectId, {
        ...(pendingTemplate ? { template: pendingTemplate } : {}),
        ...(pendingCommentary !== null ? { reviewCommentary: pendingCommentary } : {})
      });
      if (result.financials) setFinancials(result.financials);
      if (pendingTemplateRef.current === pendingTemplate) pendingTemplateRef.current = null;
      if (pendingCommentaryRef.current === pendingCommentary) {
        pendingCommentaryRef.current = null;
        setProject((previous) => ({ ...previous, review_commentary: pendingCommentary }));
      }
      setProjectSaveState(
        pendingTemplateRef.current || pendingCommentaryRef.current !== null ? 'pending' : 'saved'
      );
    } catch (err) {
      setError(errText(err));
      setProjectSaveState('error');
      if (throwOnError) throw err;
    } finally {
      setBusy(null);
    }
  };

  const queuePersistence = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistPendingChanges(), 900);
  };

  // Debounced: recompute on the server + persist the template.
  const scheduleSave = (nextTemplate, options = {}) => {
    if (!options.skipUndo && template) {
      undoTemplateRef.current = template;
    }
    setTemplate(nextTemplate);
    pendingTemplateRef.current = nextTemplate;
    setProjectSaveState('pending');
    if (!options.keepImportPreview) setSpreadImportPreview(null);
    queuePersistence();
  };

  const undoLastTemplateEdit = () => {
    if (!undoTemplateRef.current) return;
    const previousTemplate = undoTemplateRef.current;
    undoTemplateRef.current = null;
    scheduleSave(previousTemplate, { skipUndo: true });
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    setError(null);
    try {
      const extraction = await apiService.ingestProjectDocument(projectId, file);
      setProject((previous) => ({
        ...previous, extraction, source_doc_name: file.name, updated_at: new Date().toISOString(),
        ai_draft: null, status: 'draft'
      }));
      setTemplate(null);
      setFinancials(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const runDraft = async () => {
    if (spreadBlocked) {
      setError(`Resolve spread validation before regenerating. Invalid line(s): ${invalidLineSummary || 'see Spread stage'}.`);
      setActiveStage('spread');
      return;
    }
    setBusy('draft');
    setError(null);
    try {
      const result = await apiService.draftProjectProForma(projectId, guidance || undefined);
      setTemplate(result.template);
      setFinancials(result.financials);
      setProject((previous) => ({ ...previous, ai_draft: result.aiDraft }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const transitionProject = async (action, options = {}) => {
    const transitionOptions = typeof options === 'string' ? { comment: options } : (options || {});
    if (['submit', 'approve'].includes(action) && spreadBlocked) {
      setError(`Fix spread validation before ${action}. Invalid line(s): ${invalidLineSummary || 'see Spread stage'}.`);
      setActiveStage('spread');
      return;
    }
    setBusy('transition');
    setError(null);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (pendingTemplateRef.current || pendingCommentaryRef.current !== null) {
        await persistPendingChanges(true);
      }
      const result = await apiService.transitionProject(projectId, {
        action,
        actor: activeUser.name,
        comment: transitionOptions.comment || undefined,
        reviewCommentary,
        toOwner: transitionOptions.toOwner || undefined
      });
      setProject((previous) => ({
        ...previous,
        status: result.status,
        accountable_owner: result.accountableOwner || previous.accountable_owner,
        load_scenario_version: result.loadScenarioVersion || previous.load_scenario_version,
        load_stale: false,
        review_commentary: reviewCommentary,
        events: [{
          event: action,
          actor: activeUser.name,
          comment: transitionOptions.comment || null,
          created_at: new Date().toISOString(),
          payload: {
            toOwner: transitionOptions.toOwner || undefined,
            supersede: result.supersede || undefined
          }
        }, ...(previous.events || [])]
      }));
      setReturnComment('');
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const scheduleCommentarySave = (value) => {
    setReviewCommentary(value);
    pendingCommentaryRef.current = value;
    setProjectSaveState('pending');
    queuePersistence();
  };

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  useUnsavedChangesWarning(['pending', 'saving', 'error'].includes(projectSaveState));

  const loadToGl = async () => {
    if (spreadBlocked) {
      setError(`Fix spread validation before loading. Invalid line(s): ${invalidLineSummary || 'see Spread stage'}.`);
      setActiveStage('spread');
      return;
    }
    setBusy('load');
    setError(null);
    try {
      const result = await apiService.loadProjectToGl(projectId, activeUser.name);
      setProject((previous) => ({
        ...previous,
        status: 'loaded',
        load_scenario_version: result.scenarioVersion,
        load_stale: false,
        loadResult: result,
        events: [{
          event: 'load', actor: activeUser.name, created_at: new Date().toISOString(),
          payload: {
            insertedRows: result.insertedRows,
            replacedRows: result.replacedRows,
            scenarioVersion: result.scenarioVersion,
            loadBatchId: result.loadBatchId
          }
        }, ...(previous.events || [])]
      }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const flushPendingProjectChanges = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (pendingTemplateRef.current || pendingCommentaryRef.current !== null) {
      await persistPendingChanges(true);
    }
  };

  const exportSpreadWorkbook = async () => {
    setBusy('spread-export');
    setError(null);
    try {
      await flushPendingProjectChanges();
      const response = await apiService.exportProjectSpreadWorkbook(projectId);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `${project.project_dim_id || 'project'}-spread.xlsx`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const previewSpreadWorkbookImport = async (file) => {
    if (!file) return;
    setBusy('spread-import-preview');
    setError(null);
    try {
      await flushPendingProjectChanges();
      const preview = await apiService.previewProjectSpreadWorkbookImport(projectId, file, activeUser.name);
      setSpreadImportPreview(preview);
    } catch (err) {
      setError(errText(err));
      setSpreadImportPreview(null);
    } finally {
      setBusy(null);
    }
  };

  const applySpreadWorkbookImport = async (importId) => {
    if (!importId) return;
    setBusy('spread-import-apply');
    setError(null);
    try {
      const result = await apiService.applyProjectSpreadWorkbookImport(projectId, importId, activeUser.name);
      setTemplate(result.template);
      setFinancials(result.financials);
      setSpreadImportPreview(null);
      pendingTemplateRef.current = null;
      undoTemplateRef.current = null;
      setProjectSaveState('saved');
      setProject((previous) => ({
        ...previous,
        template: result.template,
        financials: result.financials,
        template_validation: result.validation,
        events: [{
          event: 'spread_import_applied',
          actor: activeUser.name,
          comment: `Applied project spread import ${importId}`,
          created_at: new Date().toISOString(),
          payload: { importId }
        }, ...(previous.events || [])]
      }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const updateLine = (collection, lineId, patch) => {
    scheduleSave({
      ...template,
      [collection]: template[collection].map((line) => {
        if (line.id !== lineId) return line;
        const reviewerEdit = project?.ai_draft && !patch.proposalStatus && (line.proposalStatus || 'pending') === 'pending'
          ? { proposalStatus: 'edited', proposalBy: activeUser.name, proposalAt: new Date().toISOString() }
          : {};
        return { ...line, ...patch, ...reviewerEdit };
      })
    });
  };
  const removeLine = (collection, lineId) => {
    if (!window.confirm('Remove this line from the project spread?')) return;
    scheduleSave({ ...template, [collection]: template[collection].filter((line) => line.id !== lineId) });
  };
  const addLine = (collection, line) => {
    scheduleSave({ ...template, [collection]: [...(template[collection] || []), line] });
  };

  // Per-line capitalization override: capOverride is an annotation on the
  // template line — absence means the derived default applies, and the
  // underlying classification arrays are never mutated. The server compute
  // honors it (expensed capex -> opex; capitalized opex -> capex + dep).
  const bucketTermMonths = (bucketId) =>
    capexBuckets.find((bucket) => bucket.id === bucketId)?.termMonths || 60;

  const setCapOverride = (collection, lineId, override, reason) => {
    // D3: a capitalization override is an accounting judgment and REQUIRES a
    // reason. The reason is recorded on the annotation and (server-side) to the
    // policy decision + project event log.
    const overrideReason = (reason || override?.reason || '').trim();
    if (!overrideReason) {
      const prompted = (typeof window !== 'undefined' && window.prompt)
        ? window.prompt('Reason for this capitalization override (required):')
        : '';
      if (!prompted || !prompted.trim()) return;
      return setCapOverride(collection, lineId, override, prompted.trim());
    }
    updateLine(collection, lineId, {
      capOverride: {
        ...override, reason: overrideReason,
        by: activeUser.label, at: new Date().toISOString()
      }
    });
  };
  const clearCapOverride = (collection, lineId) => {
    scheduleSave({
      ...template,
      [collection]: template[collection].map((line) => {
        if (line.id !== lineId) return line;
        const { capOverride, ...rest } = line;
        return rest;
      })
    });
  };

  const citations = useMemo(() => project?.extraction?.citations || [], [project?.extraction?.citations]);
  const citationById = useMemo(
    () => Object.fromEntries(citations.map((citation) => [citation.id, citation])),
    [citations]
  );
  // D2: build EVERY AI-derived proposal (not just pending) so the tracker can
  // filter/count client-side. lowRiskEligible mirrors the server rule (no policy
  // flags, not an "unknown"/exclude classification) for bulk-accept preview.
  const proposalQueue = useMemo(() => {
    if (!template || !project?.ai_draft) return [];
    const sourceEvidence = (figureId, fallback) => {
      const citation = figureId ? citationById[figureId] : null;
      return citation
        ? `Source page ${citation.page}: ${citation.quote}`
        : `Source: extracted business case · ${fallback}`;
    };
    const total = (values = []) => values.reduce((sum, value) => sum + Number(value || 0), 0);
    const lowRisk = (line, collection) => {
      if ((line.policyFlags || []).length) return false;
      const stage = String(line.policyStage || '').toLowerCase();
      if (collection === 'revenueLines') return true;
      return stage !== 'unknown' && stage !== '';
    };
    return [
      ...(template.revenueLines || []).map((line) => ({
        id: line.id,
        proposalId: `revenueLines:${line.id}`,
        collection: 'revenueLines',
        tab: 'revenue',
        type: 'marginality',
        sourceFigureId: line.sourceFigureId,
        status: line.proposalStatus || 'pending',
        title: line.description,
        currentValue: `Source hint: ${(project?.extraction?.revenueClaims || []).find((claim) => claim.figureId === line.sourceFigureId)?.marginalityHint || 'unknown'}`,
        proposedValue: line.marginal ? 'Marginal revenue' : 'Existing / excluded',
        evidence: sourceEvidence(line.sourceFigureId, line.description),
        rationale: line.exclusionReason || 'AI-proposed marginality based on the extracted business case.',
        impact: `${formatMM(total(line.byYear))} five-year revenue`,
        confidence: line.confidence,
        lowRiskEligible: lowRisk(line, 'revenueLines'),
        reviewedBy: line.proposalBy,
        reviewedAt: line.proposalAt
      })),
      ...(template.opexLines || []).map((line) => ({
        id: line.id,
        proposalId: `opexLines:${line.id}`,
        collection: 'opexLines',
        tab: 'opex',
        type: 'expense',
        sourceFigureId: line.sourceFigureId,
        status: line.proposalStatus || 'pending',
        title: line.description,
        currentValue: `Source hint: ${(project?.extraction?.spendItems || []).find((item) => item.figureId === line.sourceFigureId)?.capitalHint || 'unknown'}`,
        proposedValue: line.existing ? 'Existing / excluded' : 'Incremental operating expense',
        evidence: sourceEvidence(line.sourceFigureId, line.description),
        rationale: line.policyRationale || line.exclusionReason || 'AI-proposed expense treatment based on incremental project economics.',
        impact: `${formatMM(-total(line.byYear))} five-year pre-tax effect`,
        confidence: line.confidence,
        lowRiskEligible: lowRisk(line, 'opexLines'),
        reviewedBy: line.proposalBy,
        reviewedAt: line.proposalAt,
        policyCitation: line.policyCitation,
        policyStage: line.policyStage,
        policyConfidence: line.confidence,
        policyClassification: 'expense'
      })),
      ...(template.capexItems || []).map((line) => ({
        id: line.id,
        proposalId: `capexItems:${line.id}`,
        collection: 'capexItems',
        tab: 'capex',
        type: 'capitalization',
        sourceFigureId: line.sourceFigureId,
        status: line.proposalStatus || 'pending',
        title: line.description,
        currentValue: `Source hint: ${(project?.extraction?.spendItems || []).find((item) => item.figureId === line.sourceFigureId)?.capitalHint || 'unknown'}`,
        proposedValue: `Capitalized · ${capexBuckets.find((bucket) => bucket.id === line.bucket)?.label || line.bucket}`,
        evidence: sourceEvidence(line.sourceFigureId, line.description),
        rationale: line.policyRationale || line.rationale || 'AI-proposed capitalization bucket and useful-life treatment.',
        impact: `${formatMM(line.totalCostMM)} capitalized spend`,
        confidence: line.confidence,
        lowRiskEligible: lowRisk(line, 'capexItems'),
        reviewedBy: line.proposalBy,
        reviewedAt: line.proposalAt,
        policyCitation: line.policyCitation,
        policyStage: line.policyStage,
        policyConfidence: line.confidence,
        policyClassification: 'capitalize'
      }))
    ];
  }, [capexBuckets, citationById, project?.ai_draft, project?.extraction, template]);
  const pendingProposals = useMemo(
    () => proposalQueue.filter((proposal) => proposal.status === 'pending'),
    [proposalQueue]
  );
  const isPdfSource = (project?.source_doc_name || '').toLowerCase().endsWith('.pdf');

  const acceptProposal = (proposal) => updateLine(proposal.collection, proposal.id, {
    proposalStatus: 'accepted', proposalBy: activeUser.name, proposalAt: new Date().toISOString()
  });
  const editProposal = (proposal) => {
    const next = new URLSearchParams(projectSearchParams);
    next.set('stage', 'spread');
    next.set('spreadTab', proposal.tab);
    next.set('focusLine', proposal.id);
    setProjectSearchParams(next);
  };
  const rejectProposal = (proposal) => {
    if (!window.confirm(`Reject the AI proposal for ${proposal.title}? It will remain visible as excluded from the pro forma.`)) return;
    const exclusionPatch = proposal.collection === 'revenueLines'
      ? { marginal: false, exclusionReason: 'AI proposal rejected by reviewer.' }
      : proposal.collection === 'opexLines'
        ? { existing: true, exclusionReason: 'AI proposal rejected by reviewer.' }
        : {};
    updateLine(proposal.collection, proposal.id, {
      ...exclusionPatch,
      proposalStatus: 'rejected', proposalBy: activeUser.name, proposalAt: new Date().toISOString()
    });
  };

  // D2 bulk-accept: server accepts every pending low-risk, confident proposal in
  // one transaction and recomputes financials. Refresh the local model from the
  // returned template so charts/gates stay in sync (no parallel calc path).
  const bulkAcceptProposals = async (criteria) => {
    setBusy('bulk-accept');
    setError(null);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (pendingTemplateRef.current || pendingCommentaryRef.current !== null) {
        await persistPendingChanges(true);
      }
      await apiService.bulkAcceptProjectProposals(projectId, {
        actor: activeUser.name,
        criteria: { status: 'pending', confidenceMin: 0.85, lowRiskOnly: true, ...(criteria || {}) }
      });
      const record = await apiService.fetchProject(projectId);
      setProject(record);
      setTemplate(record.template);
      setFinancials(record.financials);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const startManualSpread = () => {
    scheduleSave({
      startMonth: 1,
      assumptions: ['Manual spread started from the reviewed source.'],
      revenueLines: [{
        id: newId(), description: 'New marginal revenue line', marginal: true,
        exclusionReason: '', byYear: [0, 0, 0, 0, 0], proposalStatus: 'manual'
      }],
      opexLines: [], capexItems: [], taxRatePct: 23.5, hurdleRatePct: 10.0
    });
    setActiveStage('spread');
  };

  const openCitation = (figureId) => {
    if (!figureId || !citationById[figureId]) return;
    setPdfOpen(true);
    setActiveCitation({ id: figureId, ts: Date.now() });
  };

  const hasSpread = Boolean(
    template && (
    (template?.revenueLines || []).length ||
    (template?.opexLines || []).length ||
    (template?.capexItems || []).length)
  );
  const pendingProposalCount = pendingProposals.length;
  const projectStages = useMemo(() => [
    {
      id: 'source', label: 'Source',
      status: extraction ? WORKSPACE_READINESS.READY : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: extraction ? 0 : 1,
      systemAction: 'Stores the source and extracts grounded claims with citations.',
      userDecision: 'Choose the authoritative business case for this project.',
      exitCriteria: 'A current source is uploaded and extraction succeeds.'
    },
    {
      id: 'extract', label: 'Extract & Classify',
      status: !extraction ? WORKSPACE_READINESS.BLOCKED
        : pendingProposalCount > 0 ? WORKSPACE_READINESS.NEEDS_ATTENTION
          : hasSpread ? WORKSPACE_READINESS.READY : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: !extraction ? 1 : pendingProposalCount,
      locked: Boolean(project) && !extraction,
      lockedReason: 'Upload and extract a business case first.',
      prerequisiteStage: 'source',
      systemAction: 'Proposes classifications and links each claim to source evidence.',
      userDecision: 'Accept, edit, or reject every AI proposal.',
      exitCriteria: 'All classifications have an explicit reviewer decision.'
    },
    {
      id: 'spread', label: 'Spread',
      status: !hasSpread ? WORKSPACE_READINESS.BLOCKED
        : spreadBlocked ? WORKSPACE_READINESS.NEEDS_ATTENTION
          : financials ? WORKSPACE_READINESS.READY : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: !hasSpread ? 1 : spreadBlocked ? spreadValidation.errors.length : 0,
      locked: Boolean(project) && !hasSpread,
      lockedReason: 'Generate or create a spread from the extracted business case first.',
      prerequisiteStage: extraction ? 'extract' : 'source',
      systemAction: 'Recalculates project economics as reviewed line items change.',
      userDecision: 'Set marginal revenue, incremental expense, and capitalization treatment.',
      exitCriteria: 'The spread is complete and computes without validation errors.'
    },
    {
      id: 'pro-forma', label: 'Pro Forma',
      status: !financials || spreadBlocked ? WORKSPACE_READINESS.BLOCKED : WORKSPACE_READINESS.READY,
      issueCount: spreadBlocked ? spreadValidation.errors.length : financials ? 0 : 1,
      locked: Boolean(project) && (!financials || spreadBlocked),
      lockedReason: spreadBlocked ? 'Fix spread validation errors first.' : 'Complete a valid project spread first.',
      prerequisiteStage: hasSpread ? 'spread' : extraction ? 'extract' : 'source',
      systemAction: 'Computes annual P&L, cash flow, NPV, payback, and NBV.',
      userDecision: 'Assess whether the economics and key drivers are credible.',
      exitCriteria: 'Financial impact is understood and ready for lifecycle review.'
    },
    {
      id: 'review', label: 'Review',
      status: !financials || pendingProposalCount > 0 || spreadBlocked ? WORKSPACE_READINESS.BLOCKED
        : project?.status === 'loaded' || project?.status === 'approved'
          ? WORKSPACE_READINESS.READY : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: (financials ? 0 : 1) + pendingProposalCount + (spreadBlocked ? spreadValidation.errors.length : 0),
      locked: Boolean(project) && (!financials || spreadBlocked),
      lockedReason: spreadBlocked ? 'Fix spread validation errors before review.' : 'Review is available after the pro forma is calculated.',
      prerequisiteStage: spreadBlocked ? 'spread' : financials ? 'pro-forma' : hasSpread ? 'spread' : extraction ? 'extract' : 'source',
      systemAction: 'Validates readiness, saves commentary, and records lifecycle events.',
      userDecision: 'Submit, return, approve, or load the reviewed project.',
      exitCriteria: 'Required checks pass and the next accountable owner is clear.'
    }
  ], [extraction, financials, hasSpread, pendingProposalCount, project, spreadBlocked, spreadValidation.errors.length]);
  const [activeStage, setActiveStage] = useWorkspaceStage(projectStages, 'source');
  const projectValidationIssues = [
    ...(!financials ? [{ id: 'financials', label: 'Calculate the project pro forma before review.', stage: 'pro-forma', severity: 'error' }] : []),
    ...(spreadBlocked ? (spreadValidation.errors || []).map((issue, index) => ({
      id: `spread-${issue.collection}-${issue.lineId}-${issue.field}-${index}`,
      label: `${issue.lineDescription || issue.lineId}: ${issue.message}`,
      detail: 'Fix the spread grid validation error before Regenerate, Pro Forma, or lifecycle advancement.',
      stage: 'spread',
      severity: 'error'
    })) : []),
    ...(pendingProposalCount > 0 ? [{
      id: 'pending-proposals',
      label: `${pendingProposalCount} AI classification proposal${pendingProposalCount === 1 ? '' : 's'} still require a reviewer decision.`,
      detail: 'Accept, edit, or reject every proposal before submitting.',
      stage: 'extract',
      severity: 'error'
    }] : [])
  ];
  const [projectSearchParams, setProjectSearchParams] = useSearchParams();
  const requestedSpreadTab = projectSearchParams.get('spreadTab');
  const spreadTab = ['revenue', 'opex', 'capex'].includes(requestedSpreadTab) ? requestedSpreadTab : 'revenue';
  const focusedLineId = projectSearchParams.get('focusLine');
  const setSpreadTab = (tab) => {
    const next = new URLSearchParams(projectSearchParams);
    next.set('spreadTab', tab);
    next.delete('focusLine');
    setProjectSearchParams(next);
  };

  useEffect(() => {
    const panel = document.getElementById(`project-stage-${activeStage}`);
    const heading = panel?.querySelector('h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, [activeStage]);

  if (!project) {
    return (
      <div className="forecast-module forecast-home">
        <section className="forecast-panel"><p className="forecast-submit-note">{error || 'Loading project…'}</p></section>
      </div>
    );
  }

  return (
    <ForecastWorkspaceShell
      eyebrow={<><Activity size={14} aria-hidden="true" /> Forecast Console / Corporate Project</>}
      title={project.name}
      description={project.description || 'Review the source, verify the AI spread, and evaluate project economics.'}
      backTo="/forecasting/projects"
      backLabel="Project Book"
      status={project.status.replace('_', ' ')}
      saveState={{ pending: 'Save pending…', saving: 'Saving…', error: 'Save failed', saved: 'Saved' }[projectSaveState]}
      metadata={[
        { label: 'Project Dimension', value: project.project_dim_id },
        { label: 'Entity', value: project.entity_id },
        { label: 'Sponsor', value: project.sponsor || '—' },
        { label: 'Accountable Owner', value: project.accountable_owner || '—' },
        { label: 'Due Date', value: project.due_date || '—' },
        { label: 'Materiality', value: project.materiality_tier ? project.materiality_tier.replace(/_/g, ' ') : '—' },
        { label: 'Source', value: project.source_doc_name || 'Not uploaded' },
        { label: 'Last Updated', value: project.updated_at ? formatTimestamp(project.updated_at) : '—' }
      ]}
      stages={projectStages}
      activeStage={activeStage}
      impactItems={financials ? [
        { id: 'npv', label: 'NPV', value: `$${financials.totals.npvMM.toFixed(1)}MM`, tone: financials.totals.npvMM >= 0 ? 'positive' : 'negative', detail: `at ${template?.hurdleRatePct || 10}%` },
        { id: 'payback', label: 'Payback', value: financials.totals.paybackLabel },
        { id: 'net-income', label: '5Y Net Income', value: `$${financials.totals.netIncomeMM.toFixed(1)}MM` },
        { id: 'capex', label: 'Capitalized Spend', value: `$${financials.totals.capexMM.toFixed(1)}MM` }
      ] : []}
    >

      {error && <div className="forecast-panel forecast-home-error" role="alert"><p>{String(error)}</p></div>}
      {['cancelled', 'archived'].includes(project.status) && (
        <div className="forecast-panel forecast-submit-note" role="status">
          This project is {project.status}. Source, spread, policy, and pro forma edits are read-only.
        </div>
      )}
      {capexBucketError && (
        <div className="forecast-panel forecast-home-error" role="alert">
          <p>Capitalization categories are unavailable. Existing work is preserved, but classification choices may be incomplete.</p>
          <small>{capexBucketError}</small>
          <button type="button" className="forecast-secondary-button compact" onClick={loadCapexBuckets}>Retry categories</button>
        </div>
      )}

      {activeStage === 'source' && (
        <CorporateProjectSourceStage
          busy={busy}
          citations={citations}
          editable={editable}
          extraction={extraction}
          fileRef={fileRef}
          isPdfSource={isPdfSource}
          onOpenPdf={() => setPdfOpen(true)}
          onUpload={upload}
          project={project}
          projectId={projectId}
        />
      )}

      {activeStage === 'extract' && (
        <CorporateProjectExtractStage
          activeCitation={activeCitation}
          aiDraft={aiDraft}
          busy={busy}
          citationById={citationById}
          editable={editable}
          extraction={extraction}
          guidance={guidance}
          hasSpread={hasSpread}
          isPdfSource={isPdfSource}
          onAcceptProposal={acceptProposal}
          onEditProposal={editProposal}
          onGuidanceChange={setGuidance}
          onOpenCitation={openCitation}
          onRejectProposal={rejectProposal}
          onBulkAccept={bulkAcceptProposals}
          onRunDraft={runDraft}
          onStartManualSpread={startManualSpread}
          proposalQueue={proposalQueue}
          pendingProposalCount={pendingProposalCount}
        />
      )}

      {template && activeStage === 'spread' && (
        <CorporateProjectSpreadStage
          activeCitation={activeCitation}
          addLine={addLine}
          bucketTermMonths={bucketTermMonths}
          capexBuckets={capexBuckets}
          canUndo={Boolean(undoTemplateRef.current)}
          citationById={citationById}
          clearCapOverride={clearCapOverride}
          durationMonths={extraction?.durationMonths}
          editable={editable}
          focusedLineId={focusedLineId}
          importPreview={spreadImportPreview}
          isPdfSource={isPdfSource}
          onApplyImport={applySpreadWorkbookImport}
          onCancelImport={() => setSpreadImportPreview(null)}
          onExport={exportSpreadWorkbook}
          onImport={previewSpreadWorkbookImport}
          onOpenCitation={openCitation}
          removeLine={removeLine}
          spreadBusy={busy}
          spreadValidation={spreadValidation}
          setCapOverride={setCapOverride}
          setSpreadTab={setSpreadTab}
          spreadTab={spreadTab}
          template={template}
          undoLastEdit={undoLastTemplateEdit}
          updateLine={updateLine}
        />
      )}

      {activeStage === 'pro-forma' && spreadBlocked && (
        <section className="forecast-panel workspace-stage-panel" id="project-stage-pro-forma" tabIndex="-1">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">PRJ_03 ● Pro forma blocked</span>
            <h2>Fix spread validation errors before reviewing server-computed financials</h2>
          </div>
          <div className="forecast-panel forecast-home-error" role="alert">
            <p>Invalid line(s): {invalidLineSummary || 'see Spread stage'}.</p>
          </div>
        </section>
      )}

      {financials && activeStage === 'pro-forma' && !spreadBlocked && (
        <React.Suspense fallback={<section className="forecast-panel"><p className="forecast-submit-note">Loading pro forma…</p></section>}>
          <CorporateProjectProFormaStage
            activeCitation={activeCitation}
            citationById={citationById}
            financials={financials}
            isPdfSource={isPdfSource}
            onOpenCitation={openCitation}
            template={template}
          />
        </React.Suspense>
      )}

      {activeStage === 'review' && (
        <CorporateProjectReviewStage
          aiDraft={aiDraft}
          busy={busy}
          editable={editable}
          loadToGl={loadToGl}
          project={project}
          projectValidationIssues={projectValidationIssues}
          returnComment={returnComment}
          reviewCommentary={reviewCommentary}
          setActiveStage={setActiveStage}
          setReturnComment={setReturnComment}
          transitionProject={transitionProject}
          updateReviewCommentary={scheduleCommentarySave}
        />
      )}

      {pdfOpen && isPdfSource && (
        <React.Suspense fallback={null}>
          <ProjectPdfPanel
            projectId={projectId}
            fileName={project.source_doc_name}
            citations={citations}
            activeCitation={activeCitation}
            onClose={() => setPdfOpen(false)}
          />
        </React.Suspense>
      )}
    </ForecastWorkspaceShell>
  );
};

export default ForecastProjectWorkspace;
